import React, { useEffect, useRef, useState } from "react";
import { Play, Radar, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  fetchHomographySourceStatus,
  fetchJob,
  setHomographySourceMode,
  startShadowBackfillAll,
  updateCameraConfig,
  fehlerText,
} from "@/lib/api";
import { fovSourceLabel } from "@/components/wizard/P3PTools";

/**
 * Phase 4 Schritt 2 · Shadow-Monitor: Aggregation der DL-Shadow-Vergleiche
 * (GET /api/admin/homography-source). Zeigt die dl_auto-Reife auf einen
 * Blick: pct_under_gate (< 5 px) ist das Aktivierungs-Gate.
 * 🆕 Plus Kamera-Kalibrier-Status pro Sequenz (FOV-Kaskade) mit Reset
 * und Ein-Klick-Backfill über alle Sequenzen.
 */
export const ShadowMonitorTile = () => {
  const [data, setData] = useState(null);
  const [backfill, setBackfill] = useState(null); // {progress, message}
  const pollRef = useRef(null);
  const load = () => fetchHomographySourceStatus().then(setData).catch(() => {});
  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, []);

  // 🆕 Ein-Klick-Backfill: alle Sequenzen mit Ankern neu vergleichen
  const onBackfillAll = async () => {
    try {
      const { job_id, n_anchors } = await startShadowBackfillAll();
      setBackfill({ progress: 0, message: `${n_anchors} Anker…` });
      pollRef.current = setInterval(async () => {
        try {
          const j = await fetchJob(job_id);
          if (j.status === "done") {
            clearInterval(pollRef.current);
            setBackfill(null);
            const r = j.result || {};
            toast.success(
              `Backfill fertig: ${r.n_measured}/${r.n_anchors} Anker messbar`,
            );
            load();
          } else if (j.status === "failed") {
            clearInterval(pollRef.current);
            setBackfill(null);
            toast.error(j.error || "Backfill fehlgeschlagen");
          } else {
            setBackfill({ progress: j.progress || 0, message: j.message || "" });
          }
        } catch {
          /* transient poll error — weiter pollen */
        }
      }, 2000);
    } catch (e) {
      toast.error(fehlerText(e) || "Backfill-Start fehlgeschlagen");
    }
  };

  // 🆕 Verpatzte Selbstkalibrierung / Override per Klick zurücksetzen
  const onResetFov = async (seqId) => {
    try {
      const d = await updateCameraConfig(seqId, {
        clear_fov_estimated: true, clear_fov_override: true,
      });
      toast.success(`FOV zurückgesetzt → ${d.camera.fov_deg}° (${fovSourceLabel(d.camera.source)})`);
      load();
    } catch (e) {
      toast.error(fehlerText(e) || "Reset fehlgeschlagen");
    }
  };

  // 🆕 2026-08 · Modus-Toggle (manual/hybrid/dl_auto). Der DB-Override
  // überlebt Restart; ``null`` löscht ihn (zurück zum ENV-Wert).
  const onSwitchMode = async (mode) => {
    if (mode === data?.mode) return;
    const gatePct = data?.deviation?.pct_under_gate ?? null;
    // dl_auto ist der Ernstfall — bei roter Gate-Quote noch mal fragen
    if (mode === "dl_auto" && gatePct != null && gatePct < 95) {
      if (!confirm(
        `Nur ${gatePct}% der Vergleiche liegen unter ${data.gate_px} px — ` +
        `dl_auto trotzdem aktivieren? DL-Auto-Anker liefern dann die ` +
        `Homographie im Broadcast-Workflow ohne manuelle Klicks.`,
      )) return;
    }
    try {
      await setHomographySourceMode(mode);
      toast.success(`Modus: ${mode}`);
      load();
    } catch (e) {
      toast.error(fehlerText(e) || "Umschaltung fehlgeschlagen");
    }
  };
  const onClearOverride = async () => {
    try {
      await setHomographySourceMode(null);
      toast.success("DB-Override gelöscht — ENV wieder aktiv.");
      load();
    } catch (e) {
      toast.error(fehlerText(e) || "Reset fehlgeschlagen");
    }
  };

  const dev = data?.deviation;
  const pct = dev?.pct_under_gate ?? null;
  const gateReady = pct != null && pct >= 95;

  // 🆕 Modell-Vergleich: rows (model_key × seq) → Pivot pro Sequenz
  const byModelSeq = data?.by_model_seq || [];
  const models = [...new Set(byModelSeq.map((r) => r.model_key))].sort();
  const seqIds = [...new Set(byModelSeq.map((r) => r.seq_id))];
  const cellOf = (sid, mk) =>
    byModelSeq.find((r) => r.seq_id === sid && r.model_key === mk);

  // 🆕 Anker-Heatmap: mm:ss-Formatter
  const fmtT = (s) =>
    s == null
      ? "—"
      : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <div data-testid="shadow-monitor-tile" className="border border-border bg-card rounded-sm p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Radar className="w-3 h-3" /> Shadow-Rollout · DL vs. Manuell
          </div>
          <h3 className="font-display font-black uppercase text-xl tracking-wider mt-1">
            dl_auto-Reife
          </h3>
        </div>
        <span
          data-testid="shadow-monitor-gate-badge"
          className={`font-mono text-[10px] uppercase tracking-widest border rounded-sm px-2 py-1 ${
            gateReady
              ? "bg-primary/10 text-primary border-primary/60"
              : "bg-secondary text-muted-foreground border-border"
          }`}
        >
          Gate &lt; {data?.gate_px ?? 5} px
        </span>
      </div>

      {/* 🆕 2026-08 · Modus-Toggle (manual/hybrid/dl_auto) — persistent
          in DB (`system_settings.homography_source`), überlebt Restart.
          dl_auto ist der Broadcast-Workflow ohne manuelle Klicks. */}
      {data?.valid_modes && (
        <div
          data-testid="homography-source-switch"
          className="mb-3 flex items-center gap-2 flex-wrap"
        >
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Modus:
          </span>
          <div className="flex rounded-sm border border-border overflow-hidden">
            {data.valid_modes.map((m) => (
              <button
                key={m}
                data-testid={`homography-source-mode-${m}`}
                onClick={() => onSwitchMode(m)}
                title={
                  m === "manual"
                    ? "Nur manuelle Anker konsumieren (kein DL-Provider)"
                    : m === "hybrid"
                      ? "Manuelle Anker + Shadow-Vergleich mit DL (kein UI-Effekt)"
                      : "DL-Auto-Anker (source=dl_auto) fließen in den Interpolierenden Provider — Broadcast-Tracking ohne manuellen Klick"
                }
                className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                  data.mode === m
                    ? "bg-cyan-400/20 text-cyan-300"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <span
            data-testid="homography-source-source"
            className="font-mono text-[9px] text-muted-foreground"
          >
            {data.mode_source === "db_override"
              ? `(DB-Override · ENV=${data.mode_env_value})`
              : "(ENV-Default)"}
          </span>
          {data.mode_source === "db_override" && (
            <button
              data-testid="homography-source-clear"
              onClick={onClearOverride}
              className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground hover:text-rose-300"
              title="DB-Override löschen — ENV-Wert wird wieder wirksam"
            >
              Reset
            </button>
          )}
        </div>
      )}

      {!data || data.n_comparisons === 0 ? (
        <div data-testid="shadow-monitor-empty" className="text-[12px] text-muted-foreground leading-relaxed">
          Noch keine Shadow-Vergleiche. Auf <span className="font-mono text-amber-300">HOMOGRAPHY_SOURCE=hybrid</span>{" "}
          schalten (jeder neue Anker vergleicht automatisch) oder per{" "}
          <span className="font-mono">POST /api/admin/shadow-backfill/&#123;seq_id&#125;</span>{" "}
          über Bestands-Anker rechnen.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <div data-testid="shadow-monitor-count" className="font-display font-black text-2xl">
                {data.n_comparisons}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                Vergleiche
              </div>
            </div>
            <div>
              <div data-testid="shadow-monitor-mean" className="font-display font-black text-2xl">
                {dev ? `${dev.mean_px}px` : "—"}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                Ø Abweichung
              </div>
            </div>
            <div>
              <div
                data-testid="shadow-monitor-pct"
                className={`font-display font-black text-2xl ${
                  gateReady ? "text-primary" : pct != null ? "text-amber-400" : ""
                }`}
              >
                {pct != null ? `${pct}%` : "—"}
              </div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                unter Gate
              </div>
            </div>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {data.n_dl_failed > 0 && (
              <span data-testid="shadow-monitor-failed" className="text-rose-300">
                {data.n_dl_failed} DL-Fails (Domain-Gap · v1-Retraining nötig)
              </span>
            )}
            {dev && (
              <span className="ml-2">p95 {dev.p95_px}px · Median {dev.median_px}px</span>
            )}
          </div>
        </>
      )}

      {/* 🆕 Modell-Vergleich: Ø-Abweichung pro Sequenz je Checkpoint (v1 vs v0) */}
      {seqIds.length > 0 && models.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border" data-testid="shadow-model-compare">
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
              Modell-Vergleich · Ø-Abweichung pro Sequenz
            </div>
            <button
              data-testid="shadow-backfill-all-btn"
              type="button"
              onClick={onBackfillAll}
              disabled={!!backfill}
              className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest border border-border rounded-sm px-2 py-1 text-muted-foreground hover:text-primary hover:border-primary/60 disabled:opacity-60 transition-colors"
              title="Alle Anker aller Sequenzen mit dem aktiven Modell neu vergleichen"
            >
              {backfill ? (
                <span data-testid="shadow-backfill-progress">
                  {Math.round((backfill.progress || 0) * 100)}% · {backfill.message}
                </span>
              ) : (
                <>
                  <Play className="w-3 h-3" /> Backfill alle
                </>
              )}
            </button>
          </div>
          <table className="w-full font-mono text-[11px]">
            <thead>
              <tr className="text-[9px] uppercase tracking-widest text-muted-foreground">
                <th className="text-left font-normal pb-1">Sequenz</th>
                {models.map((m) => (
                  <th key={m} className="text-right font-normal pb-1" title={m}>
                    {m.split("_")[0]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {seqIds.map((sid) => {
                const cells = models.map((m) => cellOf(sid, m));
                const valOf = (c) => c?.median_px ?? c?.mean_px ?? null;
                const measured = cells.filter((c) => valOf(c) != null);
                const best = measured.length
                  ? Math.min(...measured.map((c) => valOf(c)))
                  : null;
                return (
                  <tr key={sid} data-testid={`model-compare-row-${sid}`} className="border-t border-border/50">
                    <td className="py-1 pr-2 truncate max-w-[140px] text-foreground/80" title={sid}>
                      {cells.find(Boolean)?.seq_name || sid.slice(0, 8)}
                    </td>
                    {models.map((m, i) => {
                      const c = cells[i];
                      const v = valOf(c);
                      return (
                        <td
                          key={m}
                          data-testid={`model-compare-cell-${sid}-${m}`}
                          className={`py-1 text-right tabular-nums ${
                            v == null
                              ? "text-rose-300/70"
                              : v === best && measured.length > 1
                                ? "text-emerald-300 font-bold"
                                : "text-foreground/80"
                          }`}
                          title={c ? `Median ${c.median_px ?? "—"} px · Ø ${c.mean_px ?? "—"} px · ${c.n_measured}/${c.n} Anker messbar · ${c.pct_under_gate}% unter Gate` : "keine Vergleiche"}
                        >
                          {c
                            ? v != null
                              ? `${v}px (${c.n_measured}/${c.n})`
                              : `Fail (0/${c.n})`
                            : "—"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-1 font-mono text-[9px] text-muted-foreground">
            Median-Abweichung (messbar/gesamt) · Fail = DL findet keine Homographie (Domain-Gap)
          </div>
        </div>
      )}

      {/* 🆕 Anker-Heatmap: Zeitachse pro Sequenz, Marker farbcodiert nach DL-Abweichung des aktiven Modells */}
      {(data?.anchor_timeline?.length ?? 0) > 0 && (
        <div className="mt-4 pt-3 border-t border-border" data-testid="anchor-heatmap">
          <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-2">
            Anker-Heatmap · {data.anchor_timeline_model} · Zeitachse pro Sequenz
          </div>
          {data.anchor_timeline.map((row) => (
            <div
              key={row.seq_id}
              data-testid={`anchor-heatmap-row-${row.seq_id}`}
              className="flex items-center gap-2 mb-1.5 font-mono text-[10px]"
            >
              <span className="w-[120px] truncate text-foreground/80" title={row.seq_id}>
                {row.seq_name}
              </span>
              <div className="relative flex-1 h-3.5 bg-secondary/60 rounded-sm">
                {/* 🆕 Shot-Grenzen (Cuts) als Trennstriche */}
                {(row.shots || []).slice(1).map((s, si) => (
                  <span
                    key={`cut-${si}`}
                    data-testid={`anchor-heatmap-cut-${row.seq_id}-${si}`}
                    className="absolute top-0 h-full w-px bg-cyan-400/70"
                    style={{
                      left: `${row.duration_s ? Math.min(100, (s.start_s / row.duration_s) * 100) : 0}%`,
                    }}
                    title={`Schnitt → Shot ${s.shot_id} @ ${fmtT(s.start_s)}`}
                  />
                ))}
                {row.anchors.map((a, i) => {
                  const color =
                    a.deviation_px == null
                      ? "bg-zinc-500"
                      : a.deviation_px < 5
                        ? "bg-emerald-400"
                        : a.deviation_px < 30
                          ? "bg-amber-400"
                          : "bg-rose-400";
                  const left = row.duration_s
                    ? Math.min(100, (a.t_s / row.duration_s) * 100)
                    : 0;
                  return (
                    <span
                      key={i}
                      data-testid={`anchor-heatmap-dot-${row.seq_id}-${i}`}
                      className={`absolute top-0 h-full w-[5px] -ml-[2.5px] rounded-[1px] ${color}`}
                      style={{ left: `${left}%` }}
                      title={`t=${fmtT(a.t_s)} · ${
                        a.deviation_px != null
                          ? `${a.deviation_px} px`
                          : `stumm (${a.dl_error || "kein DL-H"})`
                      }`}
                    />
                  );
                })}
              </div>
              <span className="w-[38px] text-right text-[9px] text-muted-foreground">
                {fmtT(row.duration_s)}
              </span>
            </div>
          ))}
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[9px] text-muted-foreground" data-testid="anchor-heatmap-legend">
            <span><span className="inline-block w-2 h-2 rounded-[1px] bg-emerald-400 mr-1 align-middle" />&lt;5 px</span>
            <span><span className="inline-block w-2 h-2 rounded-[1px] bg-amber-400 mr-1 align-middle" />&lt;30 px</span>
            <span><span className="inline-block w-2 h-2 rounded-[1px] bg-rose-400 mr-1 align-middle" />&gt;30 px</span>
            <span><span className="inline-block w-2 h-2 rounded-[1px] bg-zinc-500 mr-1 align-middle" />stumm (kein DL-H)</span>
            <span className="text-muted-foreground/70">· Lücken ohne Marker = keine Anker → gezielt nachziehen</span>
          </div>
        </div>
      )}

      {/* 🆕 Kamera-Kalibrier-Status pro Sequenz (FOV-Kaskade) + Reset */}
      {(data?.cameras?.length ?? 0) > 0 && (
        <div className="mt-4 pt-3 border-t border-border space-y-1.5" data-testid="shadow-monitor-cameras">
          <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            Kamera-Kalibrierung (FOV-Kaskade)
          </div>
          {data.cameras.map((cam) => (
            <div
              key={cam.seq_id}
              data-testid={`shadow-monitor-camera-${cam.seq_id}`}
              className="flex items-center gap-2 font-mono text-[11px]"
            >
              <span className="truncate flex-1 text-foreground/80" title={cam.seq_id}>
                {cam.name}
              </span>
              <span
                data-testid={`shadow-monitor-camera-fov-${cam.seq_id}`}
                className={cam.source === "dlt_selfcal" ? "text-emerald-300" : "text-muted-foreground"}
              >
                Kamera: {cam.fov_deg}° · {fovSourceLabel(cam.source)}
              </span>
              {(cam.source === "dlt_selfcal" || cam.source === "manual_override") && (
                <button
                  data-testid={`camera-fov-reset-${cam.seq_id}`}
                  onClick={() => onResetFov(cam.seq_id)}
                  title="Verpatzte Kalibrierung zurücksetzen → EXIF/Profil-Standard"
                  className="flex items-center gap-1 border border-border rounded-sm px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-muted-foreground hover:text-rose-300 hover:border-rose-400/50 transition-colors"
                >
                  <RotateCcw className="w-2.5 h-2.5" /> Reset
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShadowMonitorTile;
